import {
  Bot,
  ChevronRight,
  FolderKanban,
  MessageSquare,
  PanelLeftClose,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppStore } from "../lib/store";
import type { Conversation } from "../lib/types";

interface SidebarProps {
  onOpenLibrary: (tab: "projects" | "assistants") => void;
  onCreateAssistant: () => void;
}

export function Sidebar({ onOpenLibrary, onCreateAssistant }: SidebarProps) {
  const {
    conversations,
    activeConversationId,
    assistants,
    searchQuery,
    settings,
    mobileSidebar,
    setSearchQuery,
    setMobileSidebar,
    setSettings,
    createConversation,
    selectConversation,
    deleteConversation,
    hasMoreConversations,
    loadMoreConversations,
    storageStatus,
  } = useAppStore();
  const [menuId, setMenuId] = useState<string>();
  const menuRef = useRef<HTMLDivElement>(null);

  // The row menu is absolutely positioned and otherwise stays open forever.
  useEffect(() => {
    if (!menuId) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuId(undefined);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuId(undefined);
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuId]);

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return conversations;
    // `messages` is only populated for conversations that have been opened, so
    // matching on it alone made search silently blind to most of the history.
    // `searchIndex` is the digest persisted with every conversation record.
    return conversations.filter((conversation) =>
      searchHaystack(conversation).includes(query),
    );
  }, [conversations, searchQuery]);

  const grouped = useMemo(() => groupByAge(filtered), [filtered]);
  const collapsed = settings.sidebarCollapsed;

  return (
    <>
      {mobileSidebar && (
        <button
          className="sidebar-backdrop"
          aria-label="Chiudi sidebar"
          onClick={() => setMobileSidebar(false)}
        />
      )}
      <aside
        className={`sidebar ${collapsed ? "is-collapsed" : ""} ${
          mobileSidebar ? "is-mobile-open" : ""
        }`}
      >
        <div className="brand-row">
          <div className="brand-mark">
            <span />
          </div>
          {!collapsed && (
            <div className="brand-copy">
              <strong>Vesper</strong>
              <small>private intelligence</small>
            </div>
          )}
          <button
            className="icon-button collapse-button"
            onClick={() => setSettings({ sidebarCollapsed: !collapsed })}
            title={collapsed ? "Espandi sidebar" : "Comprimi sidebar"}
            aria-label={collapsed ? "Espandi sidebar" : "Comprimi sidebar"}
          >
            {collapsed ? <ChevronRight size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        <button
          className="new-chat-button"
          onClick={createConversation}
          title="Nuova conversazione"
        >
          <Plus size={17} />
          {!collapsed && <span>Nuova conversazione</span>}
        </button>

        {!collapsed && (
          <label className="sidebar-search">
            <Search size={14} />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Cerca nella cronologia"
              aria-label="Cerca nella cronologia"
            />
          </label>
        )}

        <nav className="sidebar-nav">
          {!collapsed && <div className="section-label">Workspace</div>}
          <button
            className="sidebar-link"
            onClick={onCreateAssistant}
            title="Crea assistente"
          >
            <Sparkles size={16} />
            {!collapsed && <span>Crea assistente</span>}
          </button>
          <button
            className="sidebar-link"
            onClick={() => onOpenLibrary("assistants")}
            title="Assistenti"
          >
            <Bot size={16} />
            {!collapsed && (
              <span>
                Assistenti
                <em>{assistants.length}</em>
              </span>
            )}
          </button>
          <button
            className="sidebar-link"
            onClick={() => onOpenLibrary("projects")}
            title="Progetti"
          >
            <FolderKanban size={16} />
            {!collapsed && <span>Progetti</span>}
          </button>
        </nav>

        <div className="conversation-list">
          {grouped.map(([label, items]) => (
            <div key={label}>
              {!collapsed && <div className="section-label">{label}</div>}
              {items.map((conversation) => (
                <div
                  className={`conversation-row ${
                    conversation.id === activeConversationId ? "active" : ""
                  }`}
                  key={conversation.id}
                >
                  <button
                    className="conversation-main"
                    onClick={() => selectConversation(conversation.id)}
                    title={conversation.title}
                  >
                    <MessageSquare size={15} />
                    {!collapsed && (
                      <span>
                        <strong>{conversation.title}</strong>
                        <small>{relativeDate(conversation.updatedAt)}</small>
                      </span>
                    )}
                  </button>
                  {!collapsed && (
                    <button
                      className="conversation-menu"
                      aria-label={`Azioni per ${conversation.title}`}
                      onClick={() =>
                        setMenuId(menuId === conversation.id ? undefined : conversation.id)
                      }
                    >
                      <span className="dots" />
                    </button>
                  )}
                  {menuId === conversation.id && (
                    <div className="mini-menu" ref={menuRef}>
                      <button
                        onClick={() => {
                          deleteConversation(conversation.id);
                          setMenuId(undefined);
                        }}
                      >
                        <Trash2 size={13} /> Elimina
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
          {!collapsed && searchQuery && !filtered.length && (
            <p className="sidebar-empty">Nessuna conversazione trovata.</p>
          )}
          {!collapsed && hasMoreConversations && !searchQuery && (
            <button
              className="load-more-conversations"
              onClick={() => void loadMoreConversations()}
            >
              Carica conversazioni precedenti
            </button>
          )}
        </div>

        {!collapsed && (
          <div className="sidebar-footer">
            <div className={`privacy-dot ${storageStatus}`} />
            <div>
              <strong>IndexedDB locale</strong>
              <span>Storage {storageStatus}</span>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

function searchHaystack(conversation: Conversation): string {
  return conversation.messages.length
    ? `${conversation.title} ${conversation.messages
        .map((message) => message.content)
        .join(" ")}`.toLowerCase()
    : `${conversation.title.toLowerCase()} ${conversation.searchIndex ?? ""}`;
}

const DAY = 86_400_000;

function groupByAge(conversations: Conversation[]): Array<[string, Conversation[]]> {
  const now = Date.now();
  const buckets = new Map<string, Conversation[]>();
  for (const conversation of conversations) {
    const age = now - conversation.updatedAt;
    const label =
      age < DAY
        ? "Oggi"
        : age < 2 * DAY
          ? "Ieri"
          : age < 7 * DAY
            ? "Ultimi 7 giorni"
            : age < 30 * DAY
              ? "Ultimi 30 giorni"
              : "Prima";
    buckets.set(label, [...(buckets.get(label) ?? []), conversation]);
  }
  return [...buckets.entries()];
}

function relativeDate(timestamp: number): string {
  const diff = Date.now() - timestamp;
  if (diff < 60_000) return "ora";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min`;
  if (diff < DAY) return `${Math.floor(diff / 3_600_000)} h`;
  return new Intl.DateTimeFormat("it", { day: "2-digit", month: "short" }).format(
    timestamp,
  );
}
